import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Video,
  Mic,
  MicOff,
  VideoOff,
  MessageSquare,
  PhoneOff,
  Star,
  CheckCircle2,
  Calendar,
  Wifi,
} from 'lucide-react'
import { Button } from '@/components/ui/button'

// ============================================================================
// TYPES
// ============================================================================

type CallPhase =
  | 'searching'
  | 'available'
  | 'connecting'
  | 'on-call'
  | 'ended'

interface VirtualTechnician {
  id: number
  name: string
  specialty: string
  rating: number
  reviewCount: number
  experience: number
  ratePerMin: number
  avatar: string
  badge: string
  online: boolean
}

interface VirtualConsultStepProps {
  asset: { name: string; brand: string }
  aiSolution: string
  onClose: () => void
  onBookFollowUp: () => void
}

// ============================================================================
// MOCK DATA
// ============================================================================

const MOCK_TECHNICIANS: VirtualTechnician[] = [
  {
    id: 1,
    name: 'Marcus Rivera',
    specialty: 'Appliance Expert',
    rating: 4.9,
    reviewCount: 312,
    experience: 14,
    ratePerMin: 1.5,
    avatar: '👨‍🔧',
    badge: 'Top Rated',
    online: true,
  },
  {
    id: 2,
    name: 'Sarah Chen',
    specialty: 'HVAC & Appliances',
    rating: 4.8,
    reviewCount: 198,
    experience: 9,
    ratePerMin: 1.25,
    avatar: '👩‍🔧',
    badge: 'Fast Responder',
    online: true,
  },
  {
    id: 3,
    name: 'David Park',
    specialty: 'General Home Systems',
    rating: 4.7,
    reviewCount: 142,
    experience: 6,
    ratePerMin: 1.0,
    avatar: '🧑‍🔧',
    badge: 'Budget Friendly',
    online: true,
  },
]

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function SearchingPhase() {
  const { t } = useTranslation('aiFix')
  const [dots, setDots] = useState(0)
  const [statusIndex, setStatusIndex] = useState(0)

  const statusMessages = [
    t('virtualConsult.searchingStatus1'),
    t('virtualConsult.searchingStatus2'),
    t('virtualConsult.searchingStatus3'),
  ]

  useEffect(() => {
    const dotsInterval = setInterval(() => setDots(d => (d + 1) % 4), 500)
    const statusInterval = setInterval(
      () => setStatusIndex(i => Math.min(i + 1, statusMessages.length - 1)),
      1000
    )
    return () => {
      clearInterval(dotsInterval)
      clearInterval(statusInterval)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="flex flex-col items-center justify-center py-12 space-y-6">
      {/* Animated radar rings */}
      <div className="relative w-28 h-28 flex items-center justify-center">
        <div className="absolute inset-0 rounded-full border-2 border-[hsl(var(--brand-primary))]/20 animate-ping [animation-duration:2s]" />
        <div className="absolute inset-3 rounded-full border-2 border-[hsl(var(--brand-primary))]/30 animate-ping [animation-duration:2.5s] [animation-delay:0.4s]" />
        <div className="absolute inset-6 rounded-full border-2 border-[hsl(var(--brand-primary))]/40 animate-ping [animation-duration:3s] [animation-delay:0.8s]" />
        <div className="w-16 h-16 bg-[hsl(var(--brand-primary))]/10 rounded-full flex items-center justify-center border-2 border-[hsl(var(--brand-primary))]/30">
          <Video className="w-7 h-7 text-[hsl(var(--brand-primary))]" />
        </div>
      </div>

      <div className="text-center space-y-2">
        <p className="text-base font-semibold text-foreground">
          {statusMessages[statusIndex]}
          {'.'.repeat(dots)}
        </p>
        <p className="text-sm text-muted-foreground">
          {t('virtualConsult.searchingMatching')}
        </p>
      </div>

      {/* Signal strength animation */}
      <div className="flex items-end gap-1 h-6">
        {[1, 2, 3, 4, 5].map(i => (
          <div
            key={i}
            className={`w-2 rounded-sm bg-[hsl(var(--brand-primary))] animate-pulse`}
            style={{
              height: `${i * 4 + 4}px`,
              animationDelay: `${i * 0.1}s`,
            }}
          />
        ))}
      </div>
    </div>
  )
}

function AvailablePhase({
  technicians,
  onSelect,
}: {
  technicians: VirtualTechnician[]
  onSelect: (tech: VirtualTechnician) => void
}) {
  const { t } = useTranslation('aiFix')
  const specialtyMap: Record<string, string> = {
    'Appliance Expert': t('virtualConsult.specialtyApplianceExpert'),
    'HVAC & Appliances': t('virtualConsult.specialtyHvacAppliances'),
    'General Home Systems': t('virtualConsult.specialtyGeneralHomeSystems'),
  }
  const badgeMap: Record<string, string> = {
    'Top Rated': t('virtualConsult.badgeTopRated'),
    'Fast Responder': t('virtualConsult.badgeFastResponder'),
    'Budget Friendly': t('virtualConsult.badgeBudgetFriendly'),
  }
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
        <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
        <p className="text-sm text-green-700 dark:text-green-400 font-medium">
          {t('virtualConsult.availableBanner', { count: technicians.length })}
        </p>
      </div>

      <div className="space-y-3">
        {technicians.map(tech => (
          <button
            key={tech.id}
            onClick={() => onSelect(tech)}
            className="w-full p-4 bg-card border border-border rounded-xl hover:border-[hsl(var(--brand-primary))] hover:bg-[hsl(var(--brand-primary))]/5 hover:shadow-md active:scale-[0.99] transition-all text-left group"
          >
            <div className="flex items-start gap-4">
              {/* Avatar */}
              <div className="relative flex-shrink-0">
                <div className="w-14 h-14 bg-gradient-to-br from-[hsl(var(--brand-primary))]/20 to-blue-500/20 rounded-xl flex items-center justify-center text-2xl">
                  {tech.avatar}
                </div>
                {/* Online dot */}
                <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-500 rounded-full border-2 border-background" />
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  <p className="font-semibold text-foreground group-hover:text-[hsl(var(--brand-primary))] transition-colors">
                    {tech.name}
                  </p>
                  <span className="text-[10px] px-1.5 py-0.5 bg-[hsl(var(--brand-primary))]/10 text-[hsl(var(--brand-primary))] rounded-full font-medium">
                    {badgeMap[tech.badge] ?? tech.badge}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mb-1.5">{specialtyMap[tech.specialty] ?? tech.specialty}</p>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                    {tech.rating} ({tech.reviewCount})
                  </span>
                  <span>•</span>
                  <span>{t('virtualConsult.yearsExpShort', { years: tech.experience })}</span>
                </div>
              </div>

              {/* Rate */}
              <div className="text-right flex-shrink-0">
                <p className="text-base font-bold text-foreground">${tech.ratePerMin.toFixed(2)}</p>
                <p className="text-xs text-muted-foreground">{t('virtualConsult.perMinute')}</p>
                <div className="mt-1.5 text-xs font-medium text-green-600 dark:text-green-400 flex items-center gap-1 justify-end">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                  {t('virtualConsult.online')}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}

function ConnectingPhase({ technician }: { technician: VirtualTechnician }) {
  const { t } = useTranslation('aiFix')
  const specialtyMap: Record<string, string> = {
    'Appliance Expert': t('virtualConsult.specialtyApplianceExpert'),
    'HVAC & Appliances': t('virtualConsult.specialtyHvacAppliances'),
    'General Home Systems': t('virtualConsult.specialtyGeneralHomeSystems'),
  }
  const [step, setStep] = useState(0)
  const steps = [
    t('virtualConsult.connectingStep1'),
    t('virtualConsult.connectingStep2'),
    t('virtualConsult.connectingStep3'),
  ]

  useEffect(() => {
    const t1 = setTimeout(() => setStep(1), 1000)
    const t2 = setTimeout(() => setStep(2), 2200)
    return () => {
      clearTimeout(t1)
      clearTimeout(t2)
    }
  }, [])

  return (
    <div className="flex flex-col items-center justify-center py-10 space-y-6">
      {/* Pulsing avatar */}
      <div className="relative">
        <div className="absolute inset-0 rounded-full bg-[hsl(var(--brand-primary))]/20 animate-ping [animation-duration:1.5s]" />
        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[hsl(var(--brand-primary))]/30 to-blue-500/30 flex items-center justify-center text-4xl border-2 border-[hsl(var(--brand-primary))]/40 relative">
          {technician.avatar}
        </div>
      </div>

      <div className="text-center space-y-1">
        <p className="text-lg font-semibold text-foreground">{t('virtualConsult.connecting', { name: technician.name })}</p>
        <p className="text-sm text-muted-foreground">{specialtyMap[technician.specialty] ?? technician.specialty}</p>
      </div>

      {/* Progress steps */}
      <div className="w-full max-w-xs space-y-2">
        {steps.map((s, i) => (
          <div key={i} className="flex items-center gap-3">
            <div
              className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-500 ${
                i < step
                  ? 'bg-green-500 scale-100'
                  : i === step
                  ? 'bg-[hsl(var(--brand-primary))] animate-pulse'
                  : 'bg-muted'
              }`}
            >
              {i < step ? (
                <CheckCircle2 className="w-3 h-3 text-white" />
              ) : i === step ? (
                <div className="w-2 h-2 rounded-full bg-white animate-ping" />
              ) : null}
            </div>
            <p
              className={`text-sm transition-colors duration-500 ${
                i <= step ? 'text-foreground font-medium' : 'text-muted-foreground'
              }`}
            >
              {s}
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}

function OnCallPhase({
  technician,
  onEndCall,
}: {
  technician: VirtualTechnician
  onEndCall: (duration: number) => void
}) {
  const { t } = useTranslation('aiFix')
  const specialtyMap: Record<string, string> = {
    'Appliance Expert': t('virtualConsult.specialtyApplianceExpert'),
    'HVAC & Appliances': t('virtualConsult.specialtyHvacAppliances'),
    'General Home Systems': t('virtualConsult.specialtyGeneralHomeSystems'),
  }
  const [isMuted, setIsMuted] = useState(false)
  const [isVideoOff, setIsVideoOff] = useState(false)
  const [seconds, setSeconds] = useState(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    intervalRef.current = setInterval(() => setSeconds(s => s + 1), 1000)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [])

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60)
    const sec = s % 60
    return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
  }

  const handleEndCall = () => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    onEndCall(seconds)
  }

  const cost = ((seconds / 60) * technician.ratePerMin).toFixed(2)

  return (
    <div className="flex flex-col space-y-4">
      {/* Video area */}
      <div className="relative rounded-2xl overflow-hidden bg-zinc-900 aspect-video w-full">
        {/* Simulated video background */}
        {!isVideoOff ? (
          <div className="w-full h-full bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center">
            {/* Background tech avatar */}
            <div className="text-8xl opacity-20 select-none">{technician.avatar}</div>
            {/* Live video mock */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-6xl animate-pulse [animation-duration:3s]">{technician.avatar}</div>
            </div>
          </div>
        ) : (
          <div className="w-full h-full bg-zinc-800 flex flex-col items-center justify-center gap-3">
            <VideoOff className="w-10 h-10 text-zinc-400" />
            <p className="text-sm text-zinc-400">{t('virtualConsult.cameraOff')}</p>
          </div>
        )}

        {/* Live badge */}
        <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2 py-1 bg-red-600 rounded-full">
          <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
          <span className="text-xs font-bold text-white uppercase tracking-wider">{t('virtualConsult.live')}</span>
        </div>

        {/* Timer */}
        <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1 bg-black/60 backdrop-blur-sm rounded-full">
          <span className="text-sm font-mono font-bold text-white">{formatTime(seconds)}</span>
        </div>

        {/* Cost ticker */}
        <div className="absolute bottom-3 right-3 px-2.5 py-1 bg-black/60 backdrop-blur-sm rounded-full">
          <span className="text-xs text-zinc-300 font-mono">${cost}</span>
        </div>

        {/* Technician name overlay */}
        <div className="absolute bottom-3 left-3 flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-black/60 backdrop-blur-sm rounded-full">
            <Wifi className="w-3 h-3 text-green-400" />
            <span className="text-xs font-medium text-white">{technician.name}</span>
          </div>
        </div>

        {/* Your camera (PiP) */}
        <div className="absolute top-10 right-3 w-20 h-14 rounded-lg bg-zinc-700 border border-zinc-600 flex items-center justify-center overflow-hidden">
          {isMuted ? null : null}
          <div className="text-2xl">🧑</div>
          <div className="absolute bottom-1 left-1 right-1 text-center text-[9px] text-zinc-400">{t('virtualConsult.you')}</div>
        </div>
      </div>

      {/* Technician info strip */}
      <div className="flex items-center gap-3 px-1">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[hsl(var(--brand-primary))]/20 to-blue-500/20 flex items-center justify-center text-xl flex-shrink-0">
          {technician.avatar}
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-foreground">{technician.name}</p>
          <p className="text-xs text-muted-foreground">{specialtyMap[technician.specialty] ?? technician.specialty}</p>
        </div>
        <div className="text-right">
          <div className="flex items-center gap-1 justify-end">
            <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
            <span className="text-xs font-medium text-foreground">{technician.rating}</span>
          </div>
          <p className="text-xs text-muted-foreground">${technician.ratePerMin}/min</p>
        </div>
      </div>

      {/* Controls */}
      <div className="grid grid-cols-4 gap-3">
        {/* Mute */}
        <button
          onClick={() => setIsMuted(m => !m)}
          className={`flex flex-col items-center gap-1.5 p-3 rounded-xl transition-all ${
            isMuted
              ? 'bg-red-500/20 border border-red-500/30 text-red-500'
              : 'bg-muted border border-border text-foreground hover:bg-muted/80'
          }`}
        >
          {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
          <span className="text-[10px] font-medium">{isMuted ? t('virtualConsult.unmuteLabel') : t('virtualConsult.muteLabel')}</span>
        </button>

        {/* Video */}
        <button
          onClick={() => setIsVideoOff(v => !v)}
          className={`flex flex-col items-center gap-1.5 p-3 rounded-xl transition-all ${
            isVideoOff
              ? 'bg-red-500/20 border border-red-500/30 text-red-500'
              : 'bg-muted border border-border text-foreground hover:bg-muted/80'
          }`}
        >
          {isVideoOff ? <VideoOff className="w-5 h-5" /> : <Video className="w-5 h-5" />}
          <span className="text-[10px] font-medium">{isVideoOff ? t('virtualConsult.startVideoLabel') : t('virtualConsult.videoLabel')}</span>
        </button>

        {/* Chat */}
        <button className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-muted border border-border text-foreground hover:bg-muted/80 transition-all">
          <MessageSquare className="w-5 h-5" />
          <span className="text-[10px] font-medium">{t('virtualConsult.chatLabel')}</span>
        </button>

        {/* End Call */}
        <button
          onClick={handleEndCall}
          className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-red-600 hover:bg-red-700 text-white transition-all active:scale-95"
        >
          <PhoneOff className="w-5 h-5" />
          <span className="text-[10px] font-medium">{t('virtualConsult.endLabel')}</span>
        </button>
      </div>
    </div>
  )
}

function EndedPhase({
  technician,
  callDuration,
  onBookFollowUp,
  onClose,
}: {
  technician: VirtualTechnician
  callDuration: number
  onBookFollowUp: () => void
  onClose: () => void
}) {
  const { t } = useTranslation('aiFix')
  const specialtyMap: Record<string, string> = {
    'Appliance Expert': t('virtualConsult.specialtyApplianceExpert'),
    'HVAC & Appliances': t('virtualConsult.specialtyHvacAppliances'),
    'General Home Systems': t('virtualConsult.specialtyGeneralHomeSystems'),
  }
  const minutes = Math.floor(callDuration / 60)
  const secs = callDuration % 60
  const cost = ((callDuration / 60) * technician.ratePerMin).toFixed(2)

  return (
    <div className="flex flex-col items-center py-8 space-y-6">
      {/* Ended icon */}
      <div className="w-20 h-20 rounded-full bg-muted border-2 border-border flex items-center justify-center">
        <PhoneOff className="w-8 h-8 text-muted-foreground" />
      </div>

      <div className="text-center space-y-1">
        <p className="text-xl font-semibold text-foreground">{t('virtualConsult.callEnded')}</p>
        <p className="text-sm text-muted-foreground">
          {t('virtualConsult.durationWith', { duration: minutes > 0 ? `${minutes}m ${secs}s` : `${secs}s`, name: technician.name })}
        </p>
      </div>

      {/* Call summary card */}
      <div className="w-full p-4 bg-card border border-border rounded-xl space-y-3">
        <div className="flex items-center gap-3 pb-3 border-b border-border">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[hsl(var(--brand-primary))]/20 to-blue-500/20 flex items-center justify-center text-xl">
            {technician.avatar}
          </div>
          <div>
            <p className="font-semibold text-foreground">{technician.name}</p>
            <p className="text-xs text-muted-foreground">{specialtyMap[technician.specialty] ?? technician.specialty}</p>
          </div>
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('virtualConsult.duration')}</span>
            <span className="text-foreground font-medium">
              {minutes > 0 ? `${minutes}m ${secs}s` : `${secs}s`}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('virtualConsult.rate')}</span>
            <span className="text-foreground font-medium">${technician.ratePerMin}/min</span>
          </div>
          <div className="flex justify-between pt-2 border-t border-border">
            <span className="font-medium text-foreground">{t('virtualConsult.totalCost')}</span>
            <span className="font-bold text-foreground">${cost}</span>
          </div>
        </div>
      </div>

      {/* Rate the technician */}
      <div className="w-full space-y-2">
        <p className="text-sm font-medium text-foreground text-center">{t('virtualConsult.rateExperience')}</p>
        <div className="flex justify-center gap-2">
          {[1, 2, 3, 4, 5].map(star => (
            <button
              key={star}
              className="p-1 hover:scale-110 transition-transform"
            >
              <Star className="w-7 h-7 text-amber-400 fill-amber-400 hover:scale-110 transition-transform" />
            </button>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="w-full space-y-2">
        <Button onClick={onBookFollowUp} className="w-full h-11 font-semibold">
          <Calendar className="w-4 h-4 mr-2" />
          {t('virtualConsult.bookInPerson')}
        </Button>
        <Button onClick={onClose} variant="outline" className="w-full h-11 font-semibold">
          {t('virtualConsult.done')}
        </Button>
      </div>
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function VirtualConsultStep({
  asset,
  aiSolution,
  onClose,
  onBookFollowUp,
}: VirtualConsultStepProps) {
  const { t } = useTranslation('aiFix')
  const [phase, setPhase] = useState<CallPhase>('searching')
  const [selectedTech, setSelectedTech] = useState<VirtualTechnician | null>(null)
  const [callDuration, setCallDuration] = useState(0)

  // Auto-advance from searching → available after 3 seconds
  useEffect(() => {
    if (phase !== 'searching') return
    const t = setTimeout(() => setPhase('available'), 3000)
    return () => clearTimeout(t)
  }, [phase])

  // Auto-advance from connecting → on-call after 3.5 seconds
  useEffect(() => {
    if (phase !== 'connecting') return
    const t = setTimeout(() => setPhase('on-call'), 3500)
    return () => clearTimeout(t)
  }, [phase])

  const handleSelectTechnician = (tech: VirtualTechnician) => {
    setSelectedTech(tech)
    setPhase('connecting')
  }

  const handleEndCall = (duration: number) => {
    setCallDuration(duration)
    setPhase('ended')
  }

  // Derive the root causes from aiSolution for the issue summary
  const causes = aiSolution
    .split('\n')
    .filter(l => l.trim())
    .map(l => l.replace(/^[•\-*]+\s*/, '').trim())
    .filter(Boolean)
    .slice(0, 3)

  return (
    <div className="space-y-5">
      {/* Issue Summary - shown during searching and available phases */}
      {(phase === 'searching' || phase === 'available') && (
        <div className="p-4 bg-muted/30 border border-border rounded-xl">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            {t('virtualConsult.issueWith', { brand: asset.brand, name: asset.name })}
          </p>
          <ul className="space-y-1.5">
            {causes.length > 0 ? (
              causes.map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                  <span className="text-[hsl(var(--brand-primary))] font-bold mt-0.5">•</span>
                  <span className="flex-1 leading-relaxed">{c}</span>
                </li>
              ))
            ) : (
              <li className="text-sm text-muted-foreground">{t('virtualConsult.issueDetailsShared')}</li>
            )}
          </ul>
        </div>
      )}

      {/* Phase content */}
      {phase === 'searching' && <SearchingPhase />}

      {phase === 'available' && (
        <AvailablePhase technicians={MOCK_TECHNICIANS} onSelect={handleSelectTechnician} />
      )}

      {phase === 'connecting' && selectedTech && (
        <ConnectingPhase technician={selectedTech} />
      )}

      {phase === 'on-call' && selectedTech && (
        <OnCallPhase technician={selectedTech} onEndCall={handleEndCall} />
      )}

      {phase === 'ended' && selectedTech && (
        <EndedPhase
          technician={selectedTech}
          callDuration={callDuration}
          onBookFollowUp={onBookFollowUp}
          onClose={onClose}
        />
      )}
    </div>
  )
}
